import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'
import behaviors from '../../../util/behaviors'
import Lurker from '../../../util/lurker'

import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import PinCodeValidationBaseTemplate from '../PinCodeValidationBaseTemplate'
import { loginWithPinCode, sendPinCode } from '../../../api/scoreValidationApiCall'
import { getModalLoginExtraData } from '../../../util/experiments'
import { loginWithEmailSelfService } from '../../../api/scoreValidationApiCall'
import storage from '../../../util/storage'
import { FLOW_TYPES, LOGIN_METHOD_KEY } from '../../../util/global.consts'

const lurker = new Lurker('2fa_email')

const ErrorMsgWrapper = styled(View)`
  margin-top: 16px;
  margin-right: 0;
  margin-bottom: 16px;
  margin-left: 0;
  text-align: left;
  width: 100%;
`

const errors = {
  showGenericError: false,
  showRateLimitError: false,
  showInvalidPinCodeError: false,
  showResendCodeError: false,
  showInvalidationFailedError: false,
}

class ScoreCheckPinCodeTemplate extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      ...errors,
    }
  }

  onResendPinCodeSuccess = eventName => {
    lurker.viewMessageEvt(eventName)
    const { onResend } = this.props
    if (typeof onResend === 'function') {
      onResend()
    }
  }

  onGenericError = (error = {}, eventName = 'error') => {
    const { response: { status, data = {} } = {} } = error
    const { message, token, type } = data
    const { flow, onPhoneNotValidated, onChallengePinCodeCall, onAccountNotValidated } = this.props

    const genericErrorKey = eventName !== 'error' ? 'showResendCodeError' : 'showGenericError'

    if (status === 401) {
      if (message === 'PHONE_NOT_VALIDATED') {
        onPhoneNotValidated(token)
        lurker.redirectEvt('phone_validation', { context: flow })
        this.showError(genericErrorKey)
        behaviors.scrollToTop()
        return
      }
      if (message === 'PIN_CODE') {
        onChallengePinCodeCall(data)
        lurker.redirectEvt('pin_code', { type, context: flow })
        this.showError(genericErrorKey)
        behaviors.scrollToTop()
        return
      }
      if (message === 'ACCOUNT_NOT_VALIDATED') {
        onAccountNotValidated(token)
        lurker.viewMessageEvt(eventName, {
          accounts_reason: message.toLowerCase(),
          accounts_status_code: status,
          context: flow,
        })
        return
      }

      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'pin_code_unauthorized',
        accounts_status_code: status,
        context: flow,
      })
      this.showError(genericErrorKey)
    } else if (status === 429) {
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'pin_code_rate_limit_exceeded',
        accounts_status_code: status,
        context: flow,
      })
      this.showError('showRateLimitError')
    } else if (status === 424) {
      if (message === 'INVALIDATION_FAILED') {
        const { token } = this.props
        lurker.viewMessageEvt(eventName, {
          accounts_reason: 'invalidation_failed',
          accounts_status_code: status,
          context: flow,
        })
        this.showError('showInvalidationFailedError')
        sendPinCode(token)
      }
    } else if (status === 403) {
      switch (message) {
        case 'INVALID_PIN_CODE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'invalid_pin_code',
            accounts_status_code: status,
            context: flow,
          })
          this.showError('showInvalidPinCodeError')
          break
        case 'NO_PIN_CODE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'no_pin_code',
            accounts_status_code: status,
            context: flow,
          })
          this.showError(genericErrorKey)
          break
        case 'EXPIRED_PIN_CODE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'expired_pin_code',
            accounts_status_code: status,
            context: flow,
          })
          if (this.props.onExpiredToken) {
            this.props.onExpiredToken()
          }
          break
        default:
          lurker.viewMessageEvt(eventName, {
            accounts_reason:
              message && typeof message === 'string'
                ? `pin_code_${message.toLowerCase()}`
                : 'pin_code_unexpected_message',
            accounts_status_code: status,
            context: flow,
          })
          this.showError(genericErrorKey)
      }
    } else if (status) {
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'pin_code_unexpected_status_code',
        accounts_status_code: status,
        context: flow,
      })
      this.showError(genericErrorKey)
    } else {
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'pin_code_unexpected_error',
        accounts_error_message: (error && error.message) || 'unknown',
        context: flow,
      })
      this.showError(genericErrorKey)
    }
    behaviors.scrollToTop()
  }

  onCheckPinCodeSuccess = data => {
    const { onSuccess, modalMode, flow, abTests } = this.props

    const { publicAccountId } = data
    const loginMethod = storage.get(LOGIN_METHOD_KEY)
    storage.remove(LOGIN_METHOD_KEY)
    lurker.setPublicAccountId(publicAccountId)
    const extraData = getModalLoginExtraData(abTests, modalMode)

    // TODO - Check if we can safelly remove the email_login_success event
    // keeping it for now to avoid losing data while we validate
    lurker.viewMessageEvt('email_login_success', {
      accounts_method: 'pin_code',
      accountId: data?.accountId,
      flow: loginMethod || 'phone_2fa',
      context: flow,
      ...extraData,
    })
    lurker.viewMessageEvt('login_success', {
      accounts_method: 'pin_code',
      accountId: data?.accountId,
      flow: loginMethod || 'phone_2fa',
      context: flow,
      ...extraData,
    })

    if (typeof onSuccess === 'function') {
      onSuccess(data)
    }
  }

  handleResendPinCode = () => {
    const { token } = this.props
    this.setState({ ...errors })
    lurker.clickEvt('resend_email')
    return sendPinCode(token)
      .then(() => this.onResendPinCodeSuccess('resend_email_success'))
      .catch(error => {
        this.onGenericError(error, 'resend_email_error')
        throw error
      })
  }

  handleSubmit = pincode => {
    const { token, flow, sessionTokens } = this.props
    lurker.clickEvt('check_pincode')
    this.setState({ ...errors })
    //TODO: refactor to avoid coupling business rules on the template
    const loginApiCall =
      flow === FLOW_TYPES.SELF_SERVICE ? loginWithEmailSelfService : loginWithPinCode

    return loginApiCall(token, pincode, sessionTokens)
      .then(response => this.onCheckPinCodeSuccess(response.data))
      .catch(this.onGenericError)
  }

  showError = error => {
    this.setState({
      ...errors,
      [error]: true,
    })
  }

  renderError = () => {
    const {
      showGenericError,
      showResendCodeError,
      showInvalidPinCodeError,
      showRateLimitError,
      showInvalidationFailedError,
    } = this.state
    return (
      <React.Fragment>
        {showGenericError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Não foi possível validar seu código. Por favor, tente novamente." />
          </ErrorMsgWrapper>
        )}
        {showResendCodeError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Não foi possível reenviar o código. Por favor, tente novamente." />
          </ErrorMsgWrapper>
        )}
        {showInvalidPinCodeError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Código inválido! Confira o número que enviamos por e-mail e tente novamente." />
          </ErrorMsgWrapper>
        )}
        {showRateLimitError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs={false}
              text="Você excedeu o número de tentativas. Por favor, tente novamente mais tarde."
            />
          </ErrorMsgWrapper>
        )}
        {showInvalidationFailedError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs={false}
              text="Houve um erro com o código informado, confira seu e-mail e confirme o novo código."
            />
          </ErrorMsgWrapper>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { showInvalidPinCodeError } = this.state
    const { email, onSwitchEmail } = this.props
    return (
      <PinCodeValidationBaseTemplate
        email={email}
        hasError={showInvalidPinCodeError}
        onResend={this.handleResendPinCode}
        onSubmit={this.handleSubmit}
        onSwitchEmail={onSwitchEmail}
        renderError={this.renderError}
      />
    )
  }
}

ScoreCheckPinCodeTemplate.propTypes = {
  email: PropTypes.string.isRequired,
  flow: PropTypes.string,
  onExpiredToken: PropTypes.func,
  onResend: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  onSwitchEmail: PropTypes.func,
  token: PropTypes.string.isRequired,
  onAccountNotValidated: PropTypes.func,
  onPhoneNotValidated: PropTypes.func,
  sessionTokens: PropTypes.object,
  modalMode: PropTypes.bool,
  abTests: PropTypes.string,
}

export default ScoreCheckPinCodeTemplate
